/**
 * Single filter row – compact chip: field | operator | value | remove.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { X } from 'lucide-react';

import { LoadingSpinner } from '@/components/ui';
import { NUMERIC_OPERATORS } from '../shared/constants';
import { NO_TAGS_VALUE, TagFilterDropdown } from '@/features/smswiser-campaigns/components/shared';

interface FilterRowProps {
  filter: { field: string; operator: string; value: string };
  onUpdate: (updater: (p: FilterRowProps['filter']) => FilterRowProps['filter']) => void;
  onRemove: () => void;
  fields: Array<{ key: string; label: string; type: 'text' | 'numeric' }>;
  tagFilterOptions?: string[];
  tagFilterColors?: Record<string, string>;
  staticTagFilterOptions?: string[];
  staticTagFilterColors?: Record<string, string>;
  tagsLoading?: boolean;
  staticTagsLoading?: boolean;
  canRemove: boolean;
}

function textInputSize(value: string): number {
  const len = value.length || 5;
  return Math.max(5, Math.min(16, len + 1));
}

function numInputCh(value: string): string {
  const len = value.length || 1;
  return `${Math.max(3, Math.min(9, len + 2))}ch`;
}

export const FilterRow: FC<FilterRowProps> = ({
  filter, onUpdate, onRemove, fields,
  tagFilterOptions, tagFilterColors,
  staticTagFilterOptions, staticTagFilterColors,
  tagsLoading, staticTagsLoading, canRemove,
}) => {
  const isTagFilter = filter.field === 'tags' && tagFilterOptions != null;
  const isStaticTag = filter.field === 'staticTag' && staticTagFilterOptions != null;
  const isTagLike = isTagFilter || isStaticTag;
  const fieldDef = fields.find((f) => f.key === filter.field);
  const fieldType = fieldDef?.type ?? 'numeric';
  const tagValue = filter.value === '(none)' ? NO_TAGS_VALUE : filter.value;
  const tagLabel =
    tagValue === ''
      ? 'All tags'
      : tagValue === NO_TAGS_VALUE
        ? (isStaticTag ? 'No tag' : 'No tags')
        : tagValue;
  const isText = fieldType === 'text' || isTagLike;

  const handleFieldChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const f = fields.find((x) => x.key === e.target.value);
    onUpdate(() => ({
      field: e.target.value,
      operator: f?.type === 'numeric' ? '>=' : '=',
      value: '',
    }));
  };

  const fieldOptions = fields.map((f) => (
    <option key={f.key} value={f.key}>{f.label}</option>
  ));

  const removeBtn = canRemove && (
    <button onClick={onRemove} aria-label="Remove filter"
      className="ml-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-sm text-muted-foreground/40 transition-colors hover:bg-destructive/10 hover:text-destructive">
      <X className="h-3 w-3" />
    </button>
  );

  if (isTagLike) {
    const loading = isTagFilter ? tagsLoading : staticTagsLoading;
    return (
      <div className="flex items-center gap-1">
        <select value={filter.field} onChange={handleFieldChange}
          className="h-7 rounded-md border border-input/50 bg-background px-2 text-xs font-medium text-primary outline-none"
          aria-label="Filter field">
          {fieldOptions}
        </select>
        {loading ? (
          <span className="flex h-7 items-center gap-1 text-xs text-muted-foreground">
            <LoadingSpinner size="sm" /> Tags…
          </span>
        ) : (
          <TagFilterDropdown compact value={tagValue}
            options={(isStaticTag ? staticTagFilterOptions : tagFilterOptions) ?? []}
            tagColors={isStaticTag ? staticTagFilterColors : tagFilterColors}
            displayLabel={tagLabel}
            onSelect={(v) => onUpdate((p) => ({
              ...p, value: v === '' ? '' : v === NO_TAGS_VALUE ? '(none)' : v,
            }))}
            placeholder="All tags" />
        )}
        {removeBtn}
      </div>
    );
  }

  return (
    <div className="flex items-center">
      <div className="flex items-center overflow-hidden rounded-md border border-input/50 bg-background text-xs">
        <select value={filter.field} onChange={handleFieldChange}
          className="h-7 bg-transparent px-2 font-medium text-primary outline-none"
          aria-label="Filter field">
          {fieldOptions}
        </select>
        {fieldType === 'numeric' && (
          <>
            <span className="h-4 w-px bg-border/40" aria-hidden />
            <select value={filter.operator}
              onChange={(e) => onUpdate((p) => ({ ...p, operator: e.target.value }))}
              className="h-7 w-10 bg-transparent px-1 text-center font-mono text-muted-foreground outline-none"
              aria-label="Operator">
              {NUMERIC_OPERATORS.map((o) => (
                <option key={o.op} value={o.op}>{o.label}</option>
              ))}
            </select>
          </>
        )}
        <span className="h-4 w-px bg-border/40" aria-hidden />
        {isText ? (
          <input type="text" value={filter.value}
            onChange={(e) => onUpdate((p) => ({ ...p, value: e.target.value }))}
            placeholder="Contains…" size={textInputSize(filter.value)}
            className="h-7 min-w-0 bg-transparent px-2 font-medium text-foreground outline-none placeholder:text-muted-foreground/40"
            aria-label="Filter value" />
        ) : (
          <input type="number" value={filter.value}
            onChange={(e) => onUpdate((p) => ({ ...p, value: e.target.value }))}
            placeholder="0" style={{ width: numInputCh(filter.value) }}
            className="h-7 min-w-0 bg-transparent px-1.5 font-medium text-foreground outline-none placeholder:text-muted-foreground/40 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            aria-label="Filter value" />
        )}
      </div>
      {removeBtn}
    </div>
  );
};
